import { useState } from 'react';
import { Package, Check, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface POLine {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  ordered_qty: number;
  received_qty: number;
  remaining_qty: number;
}

interface Location {
  id: number;
  name: string;
}

interface ReceiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  poNumber: string;
  lines: POLine[];
  locations: Location[];
  onSubmit: (data: ReceiveData) => void;
  isLoading?: boolean;
}

interface ReceiveData {
  location_id: number;
  notes: string;
  lines: {
    line_id: number;
    received_qty: number;
  }[];
}

export function ReceiveDialog({
  open,
  onOpenChange,
  poNumber,
  lines,
  locations,
  onSubmit,
  isLoading,
}: ReceiveDialogProps) {
  const [locationId, setLocationId] = useState<number>(locations[0]?.id || 1);
  const [notes, setNotes] = useState('');
  const [receiveQtys, setReceiveQtys] = useState<Record<number, number>>(() =>
    lines.reduce((acc, line) => ({ ...acc, [line.id]: line.remaining_qty }), {})
  );

  const updateReceiveQty = (lineId: number, qty: number) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return;

    // Clamp to valid range
    const clampedQty = Math.max(0, Math.min(qty, line.remaining_qty));
    setReceiveQtys(prev => ({ ...prev, [lineId]: clampedQty }));
  };

  const receiveAll = () => {
    setReceiveQtys(
      lines.reduce((acc, line) => ({ ...acc, [line.id]: line.remaining_qty }), {})
    );
  };

  const clearAll = () => {
    setReceiveQtys(
      lines.reduce((acc, line) => ({ ...acc, [line.id]: 0 }), {})
    );
  };

  const totalReceiving = Object.values(receiveQtys).reduce((sum, qty) => sum + qty, 0);
  const hasItemsToReceive = totalReceiving > 0;

  const handleSubmit = () => {
    const receiveLines = Object.entries(receiveQtys)
      .filter(([, qty]) => qty > 0)
      .map(([lineId, qty]) => ({
        line_id: parseInt(lineId),
        received_qty: qty,
      }));

    if (receiveLines.length === 0) return;

    onSubmit({
      location_id: locationId,
      notes,
      lines: receiveLines,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Receive Items - {poNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Location Selection */}
          <div className="space-y-2">
            <Label>Receive Into Location</Label>
            <Select
              value={locationId.toString()}
              onValueChange={(value) => setLocationId(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id.toString()}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Line Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Items to Receive</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={receiveAll}>
                  Receive All
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
                  Clear
                </Button>
              </div>
            </div>

            <div className="rounded-lg border divide-y">
              {lines.map((line) => {
                const receiveQty = receiveQtys[line.id] || 0;
                const isComplete = receiveQty === line.remaining_qty;
                const isPartial = receiveQty > 0 && receiveQty < line.remaining_qty;

                return (
                  <div key={line.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium">{line.product_name}</p>
                        <p className="text-sm font-mono text-muted-foreground">
                          {line.sku}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-muted-foreground">
                            Ordered: <span className="font-medium">{line.ordered_qty}</span>
                          </span>
                          <span className="text-muted-foreground">
                            Received: <span className="font-medium">{line.received_qty}</span>
                          </span>
                          <span
                            className={cn(
                              'font-medium',
                              line.remaining_qty === 0 ? 'text-green-600' : 'text-blue-600'
                            )}
                          >
                            Remaining: {line.remaining_qty}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {line.remaining_qty === 0 ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="h-4 w-4" />
                            <span className="text-sm font-medium">Complete</span>
                          </div>
                        ) : (
                          <>
                            <Label className="text-sm text-muted-foreground">Receive:</Label>
                            <Input
                              type="number"
                              min="0"
                              max={line.remaining_qty}
                              value={receiveQty}
                              onChange={(e) =>
                                updateReceiveQty(line.id, parseInt(e.target.value) || 0)
                              }
                              className={cn(
                                'w-20 text-center',
                                isComplete && 'border-green-500',
                                isPartial && 'border-yellow-500'
                              )}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => updateReceiveQty(line.id, line.remaining_qty)}
                            >
                              All
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Progress bar */}
                    {line.remaining_qty > 0 && (
                      <div className="mt-3">
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-green-500 transition-all"
                            style={{
                              width: `${((line.received_qty + receiveQty) / line.ordered_qty) * 100}%`,
                            }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {line.received_qty + receiveQty} of {line.ordered_qty} will be received
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Delivery notes, quality observations, etc..."
              rows={2}
            />
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Receipt Summary</p>
                <p className="text-sm text-muted-foreground">
                  Receiving {totalReceiving} item(s) into {locations.find(l => l.id === locationId)?.name}
                </p>
              </div>
              {!hasItemsToReceive && (
                <div className="flex items-center gap-1 text-yellow-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">No items selected</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !hasItemsToReceive}
          >
            {isLoading ? 'Processing...' : `Receive ${totalReceiving} Item(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
